import {
  AppBar,
  Toolbar,
  Button,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
  useTheme,
  alpha,
  Container,
  Stack,
  Chip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import HomeIcon from "@mui/icons-material/Home";
import InfoIcon from "@mui/icons-material/Info";
import LoginIcon from "@mui/icons-material/Login";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import LanguageIcon from "@mui/icons-material/Language";
import FeaturedVideoIcon from "@mui/icons-material/FeaturedVideo";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { Link as ScrollLink } from "react-scroll";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  motion,
  useScroll,
  useTransform,
  AnimatePresence,
} from "framer-motion";

function Navbar() {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const open = Boolean(anchorEl);

  const { scrollY } = useScroll();

  // Transform values based on scroll
  const logoHeight = useTransform(scrollY, [0, 100], [48, 40]);
  const navbarMargin = useTransform(scrollY, [0, 100], [0, 20]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLanguageMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setAnchorEl(null);
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("i18nextLng", lng);
    handleLanguageMenuClose();
  };

  const isAuthPage = [
    "/login",
    "/register",
    "/ForgotPassword",
    "/sign-up",
    "/reset-password",
    "/login/sso",
    "/activate-account",
    "/term-conditions",
    "/privacy-policy",
  ].includes(location.pathname);

  const baseMenuItems = [
    {
      label: t("navbar.home"),
      type: isAuthPage ? "route" : "scroll",
      to: isAuthPage ? "/" : "home",
      icon: <HomeIcon />,
      description: t("navbar.home_desc"),
    },
  ];

  const additionalMenuItems = [
    {
      label: t("navbar.produk"),
      type: "scroll",
      to: "feature",
      icon: <FeaturedVideoIcon />,
      description: t("navbar.produk_desc"),
    },
    {
      label: t("navbar.about"),
      type: "scroll",
      to: "about",
      icon: <InfoIcon />,
      description: t("navbar.about_desc"),
    },
  ];

  const authMenuItems = [
    {
      label: t("navbar.login"),
      type: "route",
      to: "/login",
      icon: <LoginIcon />,
      description: t("navbar.login_desc"),
    },
    {
      label: t("navbar.register"),
      type: "route",
      to: "/register",
      icon: <HowToRegIcon />,
      description: t("navbar.register_desc"),
    },
  ];

  const menuItems = isAuthPage
    ? [...baseMenuItems, ...authMenuItems]
    : [...baseMenuItems, ...additionalMenuItems, ...authMenuItems];

  const drawerVariants = {
    hidden: {
      x: "100%",
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
      },
    },
    visible: {
      x: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
      },
    },
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          px: scrolled ? { xs: 2, sm: 2, md: 3 } : 0,
          // pt: scrolled ? 1 : 0,
          transition: "padding 0.3s ease",
        }}
      >
        <motion.div
          style={{
            marginTop: navbarMargin,
          }}
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <AppBar
            position="static"
            elevation={0}
            component={motion.div}
            animate={{
              borderRadius: scrolled ? "24px" : "0px",
            }}
            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            sx={{
              background: scrolled
                ? "rgba(255, 255, 255, 0.98)"
                : "rgba(255, 255, 255, 0.85)",
              backdropFilter: scrolled ? "blur(24px)" : "blur(16px)",
              paddingY: scrolled ? "20px" : "24px",
              border: scrolled ? "1px solid rgba(0,0,0,0.06)" : "none",
              borderBottom: scrolled ? "none" : "1px solid rgba(0,0,0,0.08)",
              boxShadow: scrolled
                ? "0 8px 32px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)"
                : "0 4px 16px rgba(0,0,0,0.04)",
              overflow: "hidden",
              transition: "all 0.3s ease",
            }}
          >
            <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
              <Toolbar
                disableGutters
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  minHeight: "auto !important",
                }}
              >
                {/* Logo */}
                <motion.div
                  style={{ height: logoHeight }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <Box
                    component="img"
                    src="/images/joglo.png"
                    alt="DJoglo Logo"
                    sx={{
                      height: "100%",
                      cursor: "pointer",
                      filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.08))",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.12))",
                      },
                    }}
                    onClick={() => navigate("/")}
                  />
                </motion.div>

                {/* Desktop Menu */}
                <Box
                  sx={{
                    display: { xs: "none", md: "flex" },
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  {menuItems.map((item, index) => {
                    const isRegister = item.label === t("navbar.register");
                    const isLogin = item.label === t("navbar.login");

                    if (item.type === "route") {
                      return (
                        <motion.div
                          key={index}
                          whileHover={{ scale: 1.03, y: -1 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Button
                            component={RouterLink}
                            to={item.to}
                            variant={isRegister ? "contained" : "text"}
                            endIcon={isRegister ? <ArrowForwardIcon /> : null}
                            sx={{
                              fontWeight: isRegister ? 700 : 600,
                              textTransform: "none",
                              px: isRegister ? 3 : 2,
                              py: 1,
                              borderRadius: "12px",
                              fontSize: "0.925rem",
                              letterSpacing: "0.01em",
                              transition:
                                "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                              ...(!isRegister && {
                                color: "#2c3e50",
                                "&:hover": {
                                  backgroundColor: alpha("#ff4b50", 0.08),
                                },
                                ...(isLogin && {
                                  border: "1.5px solid",
                                  borderColor: alpha("#826d63", 0.3),
                                  "&:hover": {
                                    borderColor: "#ff4b50",
                                    backgroundColor: alpha("#ff4b50", 0.05),
                                  },
                                }),
                              }),
                              ...(isRegister && {
                                background: `linear-gradient(135deg, #ff4b50 0%, #d43f44 100%)`,
                                color: "#fff",
                                boxShadow: `0 4px 16px ${alpha(
                                  "#ff4b50",
                                  0.3
                                )}`,
                                "&:hover": {
                                  boxShadow: `0 6px 24px ${alpha(
                                    "#ff4b50",
                                    0.4
                                  )}`,
                                  transform: "translateY(-2px)",
                                },
                              }),
                            }}
                          >
                            {item.label}
                          </Button>
                        </motion.div>
                      );
                    } else {
                      return (
                        <motion.div
                          key={index}
                          whileHover={{ scale: 1.03, y: -1 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Button
                            component={ScrollLink}
                            to={item.to}
                            smooth
                            duration={600}
                            offset={-100}
                            sx={{
                              fontWeight: 600,
                              textTransform: "none",
                              px: 2,
                              py: 1,
                              borderRadius: "12px",
                              fontSize: "0.925rem",
                              color: "#2c3e50",
                              letterSpacing: "0.01em",
                              transition:
                                "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                              "&:hover": {
                                backgroundColor: alpha("#000", 0.04),
                              },
                            }}
                          >
                            {item.label}
                          </Button>
                        </motion.div>
                      );
                    }
                  })}

                  {/* Language Selector */}
                  <motion.div
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Chip
                      icon={
                        <LanguageIcon sx={{ fontSize: "1.1rem !important" }} />
                      }
                      label={i18n.language.toUpperCase()}
                      onClick={handleLanguageMenuOpen}
                      sx={{
                        ml: 1,
                        height: "38px",
                        fontWeight: 700,
                        fontSize: "0.8rem",
                        letterSpacing: "0.05em",
                        backgroundColor: alpha("#000", 0.04),
                        border: "1.5px solid",
                        borderColor: alpha("#000", 0.08),
                        transition: "all 0.3s ease",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: alpha("#000", 0.08),
                          borderColor: alpha("#000", 0.15),
                          transform: "translateY(-1px)",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                        },
                      }}
                    />
                  </motion.div>

                  <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleLanguageMenuClose}
                    PaperProps={{
                      elevation: 8,
                      sx: {
                        borderRadius: 3,
                        minWidth: 220,
                        mt: 1.5,
                        overflow: "visible",
                        boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                        "&::before": {
                          content: '""',
                          display: "block",
                          position: "absolute",
                          top: 0,
                          right: 20,
                          width: 12,
                          height: 12,
                          bgcolor: "background.paper",
                          transform: "translateY(-50%) rotate(45deg)",
                          zIndex: 0,
                        },
                      },
                    }}
                  >
                    <MenuItem
                      onClick={() => changeLanguage("en")}
                      selected={i18n.language === "en"}
                      sx={{
                        borderRadius: 2,
                        mx: 1,
                        my: 0.5,
                        py: 1.5,
                        transition: "all 0.2s ease",
                        "&.Mui-selected": {
                          backgroundColor: alpha("#ff4b50", 0.08),
                          "&:hover": {
                            backgroundColor: alpha("#ff4b50", 0.12),
                          },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: i18n.language === "en" ? 700 : 500,
                            fontSize: "0.95rem",
                          }}
                        >
                          English
                        </Typography>
                        <Typography
                          sx={{
                            ml: "auto",
                            color: "text.secondary",
                            fontSize: "0.85rem",
                            fontWeight: 600,
                          }}
                        >
                          EN
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem
                      onClick={() => changeLanguage("id")}
                      selected={i18n.language === "id"}
                      sx={{
                        borderRadius: 2,
                        mx: 1,
                        my: 0.5,
                        py: 1.5,
                        transition: "all 0.2s ease",
                        "&.Mui-selected": {
                          backgroundColor: alpha("#ff4b50", 0.08),
                          "&:hover": {
                            backgroundColor: alpha("#ff4b50", 0.12),
                          },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: i18n.language === "id" ? 700 : 500,
                            fontSize: "0.95rem",
                          }}
                        >
                          Bahasa Indonesia
                        </Typography>
                        <Typography
                          sx={{
                            ml: "auto",
                            color: "text.secondary",
                            fontSize: "0.85rem",
                            fontWeight: 600,
                          }}
                        >
                          ID
                        </Typography>
                      </Box>
                    </MenuItem>
                  </Menu>
                </Box>

                {/* Mobile Menu Icon */}
                <Box
                  sx={{
                    display: { xs: "flex", md: "none" },
                    alignItems: "center",
                  }}
                >
                  <motion.div
                    whileHover={{ scale: 1.08 }}
                    whileTap={{ scale: 0.92 }}
                  >
                    <IconButton
                      onClick={() => setDrawerOpen(true)}
                      sx={{
                        color: "#2c3e50",
                        backgroundColor: alpha("#000", 0.04),
                        borderRadius: "12px",
                        p: 1.2,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          backgroundColor: alpha("#000", 0.08),
                          transform: "rotate(90deg)",
                        },
                      }}
                    >
                      <MenuIcon fontSize="medium" />
                    </IconButton>
                  </motion.div>
                </Box>
              </Toolbar>
            </Container>
          </AppBar>
        </motion.div>
      </Box>

      {/* Modern Mobile Drawer dengan AnimatePresence */}
      <AnimatePresence>
        {drawerOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={backdropVariants}
              transition={{ duration: 0.3 }}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                backdropFilter: "blur(4px)",
                zIndex: 1199,
              }}
              onClick={() => setDrawerOpen(false)}
            />

            {/* Drawer Content */}
            <motion.div
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={drawerVariants}
              style={{
                position: "fixed",
                top: 0,
                right: 0,
                bottom: 0,
                width: "min(340px, 85vw)",
                backgroundColor: "#fff",
                zIndex: 1200,
                boxShadow: "-8px 0 32px rgba(0,0,0,0.12)",
                display: "flex",
                flexDirection: "column",
                borderTopLeftRadius: "24px",
                borderBottomLeftRadius: "24px",
                overflow: "hidden",
              }}
            >
              {/* Drawer Header */}
              <Box
                sx={{
                  p: 3,
                  background: `linear-gradient(135deg, #826d63 0%, #6d5a50 100%)`,
                  color: "#fff",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    letterSpacing="0.02em"
                  >
                    Menu
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ opacity: 0.9, fontSize: "0.8rem" }}
                  >
                    Navigasi cepat
                  </Typography>
                </Box>
                <motion.div
                  whileHover={{ rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <IconButton
                    onClick={() => setDrawerOpen(false)}
                    sx={{
                      color: "#fff",
                      backgroundColor: "rgba(255,255,255,0.15)",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.25)",
                      },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </motion.div>
              </Box>

              {/* Menu Items */}
              <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
                <List sx={{ py: 0 }}>
                  {menuItems.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05, duration: 0.3 }}
                    >
                      <ListItem disablePadding sx={{ mb: 1 }}>
                        {item.type === "route" ? (
                          <ListItemButton
                            component={RouterLink}
                            to={item.to}
                            onClick={() => setDrawerOpen(false)}
                            sx={{
                              borderRadius: 2,
                              py: 1.5,
                              transition: "all 0.3s ease",
                              "&:hover": {
                                backgroundColor: alpha("#ff4b50", 0.08),
                                transform: "translateX(8px)",
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                color: "#ff4b50",
                                minWidth: 40,
                              }}
                            >
                              {item.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={item.label}
                              secondary={item.description}
                              primaryTypographyProps={{
                                fontWeight: 600,
                                fontSize: "0.95rem",
                                color: "#2c3e50",
                              }}
                              secondaryTypographyProps={{
                                fontSize: "0.75rem",
                                color: "text.secondary",
                              }}
                            />
                            <ArrowForwardIcon
                              sx={{
                                fontSize: "1.2rem",
                                color: "text.secondary",
                                opacity: 0.5,
                              }}
                            />
                          </ListItemButton>
                        ) : (
                          <ListItemButton
                            component={ScrollLink}
                            to={item.to}
                            smooth
                            duration={600}
                            offset={-100}
                            onClick={() => setDrawerOpen(false)}
                            sx={{
                              borderRadius: 2,
                              py: 1.5,
                              transition: "all 0.3s ease",
                              "&:hover": {
                                backgroundColor: alpha("#ff4b50", 0.08),
                                transform: "translateX(8px)",
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                color: "#ff4b50",
                                minWidth: 40,
                              }}
                            >
                              {item.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={item.label}
                              secondary={item.description}
                              primaryTypographyProps={{
                                fontWeight: 600,
                                fontSize: "0.95rem",
                                color: "#2c3e50",
                              }}
                              secondaryTypographyProps={{
                                fontSize: "0.75rem",
                                color: "text.secondary",
                              }}
                            />
                            <ArrowForwardIcon
                              sx={{
                                fontSize: "1.2rem",
                                color: "text.secondary",
                                opacity: 0.5,
                              }}
                            />
                          </ListItemButton>
                        )}
                      </ListItem>
                    </motion.div>
                  ))}
                </List>

                {/* Language Section */}
                <Box
                  sx={{ mt: 2, pt: 2, borderTop: "1px solid rgba(0,0,0,0.08)" }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      px: 2,
                      fontWeight: 700,
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: "0.1em",
                      fontSize: "0.7rem",
                    }}
                  >
                    Bahasa
                  </Typography>
                  <Stack spacing={1} sx={{ mt: 1.5 }}>
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <ListItemButton
                        onClick={() => {
                          changeLanguage("en");
                          setDrawerOpen(false);
                        }}
                        selected={i18n.language === "en"}
                        sx={{
                          borderRadius: 2,
                          py: 1.5,
                          backgroundColor:
                            i18n.language === "en"
                              ? alpha("#ff4b50", 0.1)
                              : "transparent",
                          "&.Mui-selected": {
                            backgroundColor: alpha("#ff4b50", 0.1),
                            "&:hover": {
                              backgroundColor: alpha("#ff4b50", 0.15),
                            },
                          },
                        }}
                      >
                        <ListItemText
                          primary="English"
                          secondary="EN"
                          primaryTypographyProps={{
                            fontWeight: i18n.language === "en" ? 700 : 500,
                            fontSize: "0.9rem",
                          }}
                          secondaryTypographyProps={{
                            fontSize: "0.75rem",
                            fontWeight: 600,
                          }}
                        />
                      </ListItemButton>
                    </motion.div>

                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <ListItemButton
                        onClick={() => {
                          changeLanguage("id");
                          setDrawerOpen(false);
                        }}
                        selected={i18n.language === "id"}
                        sx={{
                          borderRadius: 2,
                          py: 1.5,
                          backgroundColor:
                            i18n.language === "id"
                              ? alpha("#ff4b50", 0.1)
                              : "transparent",
                          "&.Mui-selected": {
                            backgroundColor: alpha("#ff4b50", 0.1),
                            "&:hover": {
                              backgroundColor: alpha("#ff4b50", 0.15),
                            },
                          },
                        }}
                      >
                        <ListItemText
                          primary="Bahasa Indonesia"
                          secondary="ID"
                          primaryTypographyProps={{
                            fontWeight: i18n.language === "id" ? 700 : 500,
                            fontSize: "0.9rem",
                          }}
                          secondaryTypographyProps={{
                            fontSize: "0.75rem",
                            fontWeight: 600,
                          }}
                        />
                      </ListItemButton>
                    </motion.div>
                  </Stack>
                </Box>
              </Box>

              {/* Drawer Footer */}
              <Box
                sx={{
                  p: 3,
                  borderTop: "1px solid rgba(0,0,0,0.08)",
                  background:
                    "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.02) 100%)",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    textAlign: "center",
                    color: "text.secondary",
                    fontSize: "0.75rem",
                  }}
                >
                  © 2024 DJoglo. All rights reserved.
                </Typography>
              </Box>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

export default Navbar;
